/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Devis\TarifsAlert.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * Composant d'alerte pour les modifications de tarifs
 * Gère la mise à jour des prix des articles avec les nouveaux tarifs
 */

import React, { useState } from 'react';
import { apiService } from '../../services/ApiService';
import { Article } from '../../pages/Devis/ConfigurateurArticle/types';

interface TarifsAlertProps {
  devis: any;
  articles: Article[];
  modifications: any[];
  onUpdateTarifs: (updatedArticles: Article[]) => void;
  onIgnore: () => void;
}

const TarifsAlert: React.FC<TarifsAlertProps> = ({ 
  devis, 
  articles, 
  modifications, 
  onUpdateTarifs, 
  onIgnore 
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdateTarifs = async () => {
    setIsUpdating(true);
    
    try {
      const articlesRecalcules: Article[] = [];
      
      // Recalculer chaque article un par un
      for (const articleExistant of articles) {
        if (articleExistant.prixCalcule) {
          try {
            
            // Recalculer le prix avec les nouveaux tarifs
            const resultatCalcul = await apiService.calculerPrix(articleExistant);
            const prixTotalArticle = resultatCalcul.prixTotalArticle || resultatCalcul.prix;
            const prixUnitaire = prixTotalArticle / articleExistant.quantite;
            
            // Mettre à jour l'article avec le nouveau prix
            const articleMisAJour: Article = {
              ...articleExistant,
              prixUnitaire: prixUnitaire,
              prixCalcule: resultatCalcul
            };
            
            articlesRecalcules.push(articleMisAJour);
          } catch (error) {
            console.error(`❌ Erreur recalcul article ${articleExistant.nom}:`, error);
            // Garder l'article avec l'ancien prix en cas d'erreur
            articlesRecalcules.push(articleExistant);
          }
        } else {
          // Article libre sans calcul, garder tel quel
          articlesRecalcules.push(articleExistant);
        }
      }
      
      onUpdateTarifs(articlesRecalcules);
    } catch (error) {
      console.error('❌ TarifsAlert: Erreur lors de la mise à jour des tarifs:', error);
      // En cas d'erreur globale, on ignore la mise à jour
      onIgnore();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleIgnore = () => {
    onIgnore();
  };

  return (
    <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-orange-800">
            Modifications de tarifs détectées
          </h3>
          <div className="mt-2 text-sm text-orange-700">
            <p>
              Des modifications ont été apportées aux tarifs après la dernière modification de ce devis. 
              Souhaitez-vous mettre à jour ce devis avec ces nouveaux tarifs ?
            </p>
            
            {/* Détail des modifications */}
            {modifications.length > 0 && (
              <div className="mt-3">
                <p className="font-medium">Modifications récentes :</p>
                <ul className="mt-1 list-disc list-inside">
                  {modifications.slice(0, 3).map((modif, index) => (
                    <li key={index} className="text-xs">
                      {modif.description || modif.type_modification} ({new Date(modif.date_modification || modif.date).toLocaleDateString('fr-FR')})
                    </li>
                  ))}
                  {modifications.length > 3 && (
                    <li className="text-xs italic">
                      ... et {modifications.length - 3} autre(s) modification(s)
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          
          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              className="bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleUpdateTarifs}
              disabled={isUpdating}
            >
              {isUpdating ? 'Mise à jour en cours...' : 'Mettre à jour les tarifs'}
            </button>
            <button
              type="button"
              className="bg-white text-orange-800 px-4 py-2 rounded-md text-sm font-medium border border-orange-300 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
              onClick={handleIgnore}
              disabled={isUpdating}
            >
              Ignorer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TarifsAlert;
